'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-tabs.scss';
import { useEffect, useRef } from 'react';
import { HorizontalScrollArea } from '../../atoms/horizontal-scroll-area/horizontal-scroll-area.tsx';
import { NavLink } from '../../atoms/link/nav-link.tsx';

/**
 * Positions the active tab within the visible scroll area on mobile.
 * For now we're just applying this for all screen sizes if focusOnMobile is true.
 */
const anchorActiveTab = (container: HTMLDivElement, list: HTMLDivElement): void => {
  if (!container || !list?.childElementCount) {
    return;
  }

  // Find the active tab. NavLink and the isActive override both emit
  // aria-current="page", so match that value.
  const activeTab = list.querySelector<HTMLElement>('[aria-current="page"]');
  if (!activeTab) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const activeTabRect = activeTab.getBoundingClientRect();
  const isActiveTabVisible = activeTabRect.left >= containerRect.left && activeTabRect.right <= containerRect.right;
  if (isActiveTabVisible) {
    return;
  }
  const lastTab = list.children[list.childElementCount - 1];
  const containerWidth = container.clientWidth;
  const maxScrollLeft = Math.max(0, container.scrollWidth - containerWidth);
  const tabOffsetLeft = activeTab.offsetLeft;
  const tabWidth = activeTab.offsetWidth;
  let targetLeft = tabOffsetLeft - (containerWidth - tabWidth) / 2;
  if (lastTab === activeTab) {
    targetLeft = tabOffsetLeft + tabWidth - containerWidth;
  }
  container.scrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);
};
export type LinkPlaceholder = {
  id: string;
  title: string;
  path: string;
  /**
   * In almost all cases this should be automatically handled by the Link component.
   */
  isActive?: boolean;
};
export type NavTabsProps = {
  links: LinkPlaceholder[];
  focusOnMobile?: boolean;
};

// TODO: accessibility considerations we need to have in mind for these tertiary navs
export const NavTabs: React.FC<NavTabsProps> = t0 => {
  const $ = _c(7);
  const {
    links,
    focusOnMobile: t1
  } = t0;
  const focusOnMobile = t1 === undefined ? true : t1;
  const scrollContainerRef = useRef(null);
  const listRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== focusOnMobile) {
    t2 = () => {
      if (!focusOnMobile) {
        return;
      }
      const container = scrollContainerRef.current;
      const list = listRef.current;
      if (!container || !list) {
        return;
      }
      if (globalThis.MutationObserver === undefined) {
        anchorActiveTab(container, list);
        return;
      }
      const anchorActiveTabWhenSelected = () => {
        anchorActiveTab(container, list);
      };
      anchorActiveTabWhenSelected();
      let animationFrameId;
      const activeTabObserver = new MutationObserver(mutations => {
        if (mutations.some(_temp)) {
          if (animationFrameId !== undefined) {
            cancelAnimationFrame(animationFrameId);
          }
          animationFrameId = requestAnimationFrame(() => {
            animationFrameId = undefined;
            anchorActiveTabWhenSelected();
          });
        }
      });
      activeTabObserver.observe(list, {
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-current"]
      });
      return () => {
        activeTabObserver.disconnect();
        if (animationFrameId !== undefined) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    };
    t3 = [focusOnMobile];
    $[0] = focusOnMobile;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (!links || links.length === 0) {
    return null;
  }
  let t4;
  if ($[3] !== links) {
    t4 = links.map(_temp2);
    $[3] = links;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <nav className="NavTabs mb-comfy-xl relative w-100"><HorizontalScrollArea><div ref={scrollContainerRef} className="NavTabs__scrollcontainer overflow-x-auto"><div ref={listRef} className="NavTabs__list flex gap-comfy-md">{t4}</div></div></HorizontalScrollArea></nav>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
function _temp(t0) {
  const {
    type,
    attributeName
  } = t0;
  return type === "attributes" && attributeName === "aria-current";
}
function _temp2(link) {
  return <NavLink key={link.id} href={link.path} className="NavTabs__tab label-potent label-lg pr-snug-md py-comfy-xs pl-snug-md flex-shrink-0" {...link.isActive && {
    "aria-current": "page"
  }}>{link.title}</NavLink>;
}